import { feedbackSchema, type FeedbackInput } from '@helpdesk/shared';
import { useState, type FormEvent } from 'react';
import { validate, type FieldErrors } from '../lib/validate.ts';
import { Button } from './ui/button.tsx';
import { ChoiceCards, Field, Textarea } from './ui/form.tsx';
import { Alert } from './ui/misc.tsx';
import { StarRating } from './ui/pickers.tsx';

const DETAIL_QUESTIONS = [
  ['responseTime', 'How quickly did IT respond?'],
  ['resolutionQuality', 'How well was the problem fixed?'],
  ['professionalism', 'How professional was the IT officer?'],
] as const;

/**
 * The overall rating is the only required answer. `satisfaction` starts it
 * off when the person already tapped a star in the email.
 */
export function FeedbackForm({
  onSubmit,
  pending,
  serverError,
  satisfaction,
}: {
  onSubmit: (input: FeedbackInput) => void;
  pending: boolean;
  serverError?: string;
  satisfaction?: number;
}) {
  const [ratings, setRatings] = useState<Record<string, number>>(satisfaction ? { satisfaction } : {});
  const [recommend, setRecommend] = useState('');
  const [comments, setComments] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(feedbackSchema, {
      ...ratings,
      wouldRecommend: recommend === '' ? undefined : recommend === 'yes',
      comments,
    });
    if (!result.ok) return setErrors(result.fields);
    setErrors({});
    onSubmit(result.data);
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {serverError && <Alert tone="error">{serverError}</Alert>}
      <StarRating
        label="Overall, how satisfied are you?"
        value={ratings.satisfaction ?? 0}
        onChange={(n) => setRatings((r) => ({ ...r, satisfaction: n }))}
        error={errors.satisfaction}
      />
      <p className="border-t border-line pt-4 text-sm font-medium text-muted">
        A little more detail, if you like
      </p>
      {DETAIL_QUESTIONS.map(([key, label]) => (
        <StarRating
          key={key}
          label={label}
          value={ratings[key] ?? 0}
          onChange={(n) => setRatings((r) => ({ ...r, [key]: n }))}
          error={errors[key]}
        />
      ))}
      <ChoiceCards
        name="wouldRecommend"
        label="Would you recommend the IT service to a colleague?"
        options={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]}
        value={recommend}
        onChange={setRecommend}
        error={errors.wouldRecommend}
        required={false}
      />
      <Field label="Anything else you’d like to tell us?" error={errors.comments} hint="Optional">
        <Textarea value={comments} onChange={(e) => setComments(e.target.value)} maxLength={1000} rows={3} />
      </Field>
      <Button type="submit" loading={pending}>
        Send feedback
      </Button>
    </form>
  );
}
